import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, AlertTriangle, Globe2, MapPin, Plus, Search, Users } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { useNotice } from "../shared/useNotice";
import { AccessRole, ocpiApi, Overview, PartnerRow } from "../../../api/maintenance/ocpi";
import { ago, canEditOcpi, errText, Modal, MODULE_LABEL, StatePill } from "./ocpiShared";
import OcpiSubnav from "./OcpiSubnav";
import "../shared/maintenance-analytics.scss";
import "../stations/maintenance-stations.scss";
import "./ocpi.scss";

const OcpiPartners: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<PartnerRow[]>([]);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [roles, setRoles] = useState<AccessRole[]>([]);
  const [search, setSearch] = useState("");
  const [state, setState] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useNotice("error", "");
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, o, r] = await Promise.all([ocpiApi.partners(search, state), ocpiApi.overview(), ocpiApi.roles()]);
      setRows(p.data);
      setOverview(o.data);
      setRoles(r.data);
    } catch (e: any) {
      setError(errText(e, "Failed to load OCPI partners"));
    } finally {
      setLoading(false);
    }
  }, [search, state, setError]);

  useEffect(() => { load(); }, [load]);

  const c = overview?.counts;
  return (
    <MaintenanceLayout title="OCPI Roaming">
      <div className="ma">
        <OcpiSubnav />
        {overview && !overview.settings?.party_id && (
          <div className="ma-error">
            Our OCPI party ID is not set, so partners cannot register yet. Set it under <a href="/maintenance/ocpi/settings">Settings</a>.
          </div>
        )}
        {c && (
          <div className="ma-kpis">
            <Kpi icon={<Users size={15} />} label="Partners" value={`${c.registered} / ${c.partners}`} sub={`connected / total${c.suspended ? ` · ${c.suspended} suspended` : ""}`} />
            <Kpi icon={<MapPin size={15} />} label="Published locations" value={c.locations} sub={`${c.evses} EVSEs`} />
            <Kpi icon={<Activity size={15} />} label="Requests (24h)" value={c.requests_24h.toLocaleString()} sub="all partners, both directions" />
            <Kpi icon={<Globe2 size={15} />} label="Roaming sessions" value={c.active_sessions} sub={`${c.cdrs_30d} CDRs in 30 days`} />
            {c.connector_types_to_review > 0 && (
              <Kpi icon={<AlertTriangle size={15} />} label="Connector types to review" value={c.connector_types_to_review}
                   sub="best-effort mappings" onClick={() => navigate("/maintenance/ocpi/settings")} />
            )}
          </div>
        )}
        <div className="ma-toolbar">
          <label className="ma-search"><Search size={14} />
            <input placeholder="Search partners" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search partners" />
          </label>
          <label className="ma-select">Status
            <select value={state} onChange={(e) => setState(e.target.value)}>
              <option value="">All</option>
              <option value="NEW">No key yet</option>
              <option value="PENDING">Awaiting partner</option>
              <option value="REGISTERED">Connected</option>
              <option value="UNREGISTERED">Disconnected</option>
            </select>
          </label>
          <span className="ma-spacer" />
          {canEditOcpi() && (
            <button className="ma-btn primary" onClick={() => setShowCreate(true)}><Plus size={14} /> New partner</button>
          )}
        </div>
        {error && <div className="ma-error">{error}</div>}
        <div className="ma-card">
          <div className="ma-tablewrap">
            <table className="ma-table">
              <thead>
                <tr>
                  <th>Partner</th><th>Access role</th><th>Status</th><th>OCPI parties</th><th>Last seen</th>
                  <th className="num">Requests 24h</th><th className="num">Errors 24h</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="ma-loading">Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={7} className="ma-empty">No partners yet. Create one, pick its access role, then issue its registration key.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="clickable" onClick={() => navigate(`/maintenance/ocpi/partners/${r.id}`)}>
                    <td><strong>{r.name}</strong></td>
                    <td>{r.role_name}</td>
                    <td><StatePill state={r.registration_state} suspended={r.is_suspended} /></td>
                    <td className="sub">{r.parties || "—"}</td>
                    <td title={r.last_seen_at || ""}>{ago(r.last_seen_at)}</td>
                    <td className="num">{r.requests_24h.toLocaleString()}</td>
                    <td className="num">
                      {r.requests_24h ? `${((100 * r.errors_24h) / r.requests_24h).toFixed(1)}%` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {showCreate && (
        <CreatePartner roles={roles} onClose={() => setShowCreate(false)}
                       onCreated={(id) => navigate(`/maintenance/ocpi/partners/${id}?new=1`)} />
      )}
    </MaintenanceLayout>
  );
};

const Kpi: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; sub?: string; onClick?: () => void }> = ({
  icon, label, value, sub, onClick,
}) => (
  <div className="ma-kpi" onClick={onClick} style={onClick ? { cursor: "pointer" } : undefined}>
    <div className="ma-kpi-ic" style={{ background: "#eef0ff", color: "#5554d3" }}>{icon}</div>
    <div className="ma-kpi-label">{label}</div>
    <div className="ma-kpi-value">{value}</div>
    {sub && <div className="ma-kpi-sub">{sub}</div>}
  </div>
);

const CreatePartner: React.FC<{ roles: AccessRole[]; onClose: () => void; onCreated: (id: number) => void }> = ({
  roles, onClose, onCreated,
}) => {
  const active = useMemo(() => roles.filter((r) => !r.is_archived), [roles]);
  const [name, setName] = useState("");
  const [roleId, setRoleId] = useState<number | "">(active.find((r) => r.role_key === "navigation")?.id ?? "");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [scope, setScope] = useState<"ALL" | "SELECTED" | "NONE">("ALL");
  const [ips, setIps] = useState("");
  const [notes, setNotes] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const role = active.find((r) => r.id === roleId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !roleId) { setErr("Name and access role are required"); return; }
    setSaving(true);
    setErr("");
    try {
      const allowed = ips.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
      const res = await ocpiApi.createPartner({
        name: name.trim(), access_role_id: Number(roleId), contact_name: contactName || null,
        contact_email: contactEmail || null, notes: notes || null, location_scope: scope,
        allowed_ips: allowed.length ? allowed : null,
      });
      onCreated(res.data.id);
    } catch (e: any) {
      setErr(errText(e, "Failed to create partner"));
      setSaving(false);
    }
  };

  return (
    <Modal title="New OCPI partner" onClose={onClose} wide>
      <form onSubmit={submit}>
        <div className="ocpi-form-grid">
          <label>Partner name<input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} required /></label>
          <label>Access role
            <select value={roleId} onChange={(e) => setRoleId(e.target.value ? Number(e.target.value) : "")} required>
              <option value="">Select…</option>
              {active.map((r) => <option key={r.id} value={r.id}>{r.name}{r.is_system ? "" : " (custom)"}</option>)}
            </select>
          </label>
          <label>Contact name<input value={contactName} onChange={(e) => setContactName(e.target.value)} maxLength={120} /></label>
          <label>Contact email<input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} maxLength={255} /></label>
          <label>Stations shared
            <select value={scope} onChange={(e) => setScope(e.target.value as any)}>
              <option value="ALL">All public stations</option>
              <option value="SELECTED">Only selected stations</option>
              <option value="NONE">None (yet)</option>
            </select>
          </label>
          <label>Allowed source IPs (optional)
            <input placeholder="e.g. 203.0.113.0/24, 198.51.100.7" value={ips} onChange={(e) => setIps(e.target.value)} />
          </label>
        </div>
        {role && (
          <div className="ma-note" style={{ marginTop: 12 }}>
            <strong>{role.name}</strong> — {role.description}
            <div className="ocpi-chips" style={{ marginTop: 6 }}>
              {role.permissions.map((p) => <span key={p} className="on">{MODULE_LABEL[p.split(":")[0]] || p.split(":")[0]}: {p.split(":")[1]}</span>)}
            </div>
            <div style={{ marginTop: 6 }}>
              Limits: {role.requests_per_minute}/min (burst {role.burst}){role.commands_per_minute ? `, ${role.commands_per_minute} commands/min` : ", no remote commands"}, page size {role.max_page_size}.
              You can tighten or widen this partner later without changing the role.
            </div>
          </div>
        )}
        <div className="ocpi-form-grid" style={{ gridTemplateColumns: "1fr", marginTop: 10 }}>
          <label>Notes<textarea value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={4000} /></label>
        </div>
        {err && <p className="maint-form-error">{err}</p>}
        <div className="maint-form-actions">
          <button type="button" className="maint-btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="maint-btn-primary" disabled={saving}>{saving ? "Creating…" : "Create partner"}</button>
        </div>
      </form>
    </Modal>
  );
};

export default OcpiPartners;
